import React, { Fragment, useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Menu, MenuItem, Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { withTranslation } from "react-i18next";
import StandardService from "../../core/service/StandardService";
import Utils from "../../core/utilities/Utils";
import { toast } from "react-toastify";

export const MOVE_CONFIGURATION_TYPES = {
  METRICS_TO_TOPIC: "METRICS_TO_TOPIC",
  DATAPOINTS_TO_METRIC: "DATAPOINTS_TO_METRIC",
};

const INITIAL_MODAL_STATE = {
  isLoadingTopics: false,
  isLoadingMetrics: false,
  isMoving: false,
  destinationTopicId: "",
  destinationMetricId: "",
  topicOptions: [],
  metricOptions: [],
};

const getTopicCategoryLabel = (t, categoryType) => {
  switch (categoryType) {
    case "SOCIAL":
      return t("Social.label");
    case "ENVIRONMENTAL":
      return t("Environmental.label");
    case "GOVERNANCE":
      return t("Governance.label");
    default:
      return Utils.toCamelCase(categoryType);
  }
};

const TOPIC_CATEGORY_ORDER = ["ENVIRONMENTAL", "SOCIAL", "GOVERNANCE"];

const getTopicCategorySortIndex = (categoryType) => {
  const index = TOPIC_CATEGORY_ORDER.indexOf(categoryType);
  return index === -1 ? TOPIC_CATEGORY_ORDER.length : index;
};

const buildAvailableTopics = (topics, excludeTopicId) => (
  (topics || [])
    .filter(topic => !excludeTopicId || String(topic.id) !== String(excludeTopicId))
    .sort((a, b) => {
      const categoryDiff = getTopicCategorySortIndex(a.categoryType) - getTopicCategorySortIndex(b.categoryType);
      return categoryDiff !== 0 ? categoryDiff : (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    })
);

const getAvailableMetrics = (metrics, excludeMetricId, quantitativeOnly = false) => (
  (metrics || [])
    .filter(metric => !excludeMetricId || String(metric.id) !== String(excludeMetricId))
    .filter(metric => !quantitativeOnly || metric.category === "QUANTITATIVE")
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
);

const MoveConfigurationModal = ({
  t,
  show,
  onHide,
  moveType,
  standardId,
  selectedIds = [],
  sourceName = "",
  excludeTopicId,
  excludeMetricId,
  onMoveSuccess,
}) => {
  const [modalState, setModalState] = useState(INITIAL_MODAL_STATE);
  const isDatapointsMove = moveType === MOVE_CONFIGURATION_TYPES.DATAPOINTS_TO_METRIC;
  const translationPrefix = isDatapointsMove ? "DataPoints.moveTo" : "Metrics.moveTo";

  useEffect(() => {
    if (!show) {
      setModalState(INITIAL_MODAL_STATE);
      return;
    }

    if (!standardId) {
      return;
    }

    setModalState({ ...INITIAL_MODAL_STATE, isLoadingTopics: true });

    StandardService.getTopics(standardId)
      .then((response) => {
        const topics = response?.data?.topics;
        setModalState(prev => ({
          ...prev,
          topicOptions: Array.isArray(topics) ? topics : [],
          isLoadingTopics: false,
        }));
      })
      .catch(() => {
        setModalState(prev => ({
          ...prev,
          topicOptions: [],
          isLoadingTopics: false,
        }));
      });
  }, [show, standardId]);

  const loadMetricsForTopic = (topicId) => {
    if (!standardId || !topicId) {
      setModalState(prev => ({
        ...prev,
        metricOptions: [],
        isLoadingMetrics: false,
      }));
      return;
    }

    setModalState(prev => ({
      ...prev,
      isLoadingMetrics: true,
      destinationMetricId: "",
      metricOptions: [],
    }));

    StandardService.getMetrics(standardId, topicId)
      .then((response) => {
        const metrics = getAvailableMetrics(
          response?.data?.metrics,
          excludeMetricId,
          isDatapointsMove
        );
        setModalState(prev => ({
          ...prev,
          metricOptions: metrics,
          isLoadingMetrics: false,
        }));
      })
      .catch(() => {
        setModalState(prev => ({
          ...prev,
          metricOptions: [],
          isLoadingMetrics: false,
        }));
      });
  };

  const handleTopicChange = (topicId) => {
    if (isDatapointsMove) {
      setModalState(prev => ({
        ...prev,
        destinationTopicId: topicId,
        destinationMetricId: "",
        metricOptions: [],
      }));
      loadMetricsForTopic(topicId);
      return;
    }

    setModalState(prev => ({
      ...prev,
      destinationTopicId: topicId,
    }));
  };

  const handleMove = () => {
    const { destinationTopicId, destinationMetricId, isMoving } = modalState;
    if (selectedIds.length === 0 || isMoving) {
      return;
    }

    if (isDatapointsMove) {
      if (!destinationMetricId) {
        return;
      }
    } else if (!destinationTopicId) {
      return;
    }

    setModalState(prev => ({ ...prev, isMoving: true }));

    const moveRequest = isDatapointsMove
      ? StandardService.moveDataPointsToMetric(destinationMetricId, selectedIds)
      : StandardService.moveMetricsToTopic(destinationTopicId, selectedIds);

    moveRequest
      .then((response) => {
        if (response.status === 200) {
          toast.success(t(`${translationPrefix}.success.label`), {
            position: toast.POSITION.TOP_RIGHT,
          });
          onMoveSuccess?.();
          onHide?.();
        }
      })
      .catch(() => { })
      .finally(() => {
        setModalState(prev => ({ ...prev, isMoving: false }));
      });
  };

  const availableTopics = buildAvailableTopics(modalState.topicOptions, excludeTopicId);
  const selectedTopic = availableTopics.filter(
    topic => String(topic.id) === String(modalState.destinationTopicId)
  );
  const selectedMetric = modalState.metricOptions.filter(
    metric => String(metric.id) === String(modalState.destinationMetricId)
  );

  const renderGroupedTopicMenu = (results, menuProps) => {
    const grouped = results.reduce((acc, topic) => {
      const categoryType = topic.categoryType || "OTHER";
      if (!acc[categoryType]) {
        acc[categoryType] = [];
      }
      acc[categoryType].push(topic);
      return acc;
    }, {});

    const orderedCategories = Object.keys(grouped).sort(
      (a, b) => getTopicCategorySortIndex(a) - getTopicCategorySortIndex(b)
    );

    let position = 0;
    return (
      <Menu {...menuProps}>
        {orderedCategories.map((categoryType, index) => (
          <Fragment key={categoryType}>
            {index > 0 && <Menu.Divider />}
            <Menu.Header>{getTopicCategoryLabel(t, categoryType)}</Menu.Header>
            {grouped[categoryType].map((topic) => {
              const item = (
                <MenuItem key={topic.id} option={topic} position={position} title={topic.name}>
                  {topic.name}
                </MenuItem>
              );
              position += 1;
              return item;
            })}
          </Fragment>
        ))}
      </Menu>
    );
  };

  const summaryValues = isDatapointsMove
    ? { count: selectedIds.length, metricName: sourceName }
    : { count: selectedIds.length, topicName: sourceName };

  const isMoveDisabled = isDatapointsMove
    ? !modalState.destinationMetricId
      || modalState.isMoving
      || modalState.isLoadingTopics
      || modalState.isLoadingMetrics
    : !modalState.destinationTopicId
      || modalState.isMoving
      || modalState.isLoadingTopics;

  const topicPlaceholder = modalState.isLoadingTopics
    ? t(isDatapointsMove
      ? `${translationPrefix}.loadingTopics.label`
      : `${translationPrefix}.loading.label`)
    : `${translationPrefix}.selectTopic.placeholder.label`;

  const topicFieldLabel = isDatapointsMove
    ? t(`${translationPrefix}.topic.label`)
    : t(`${translationPrefix}.destination.label`);

  return (
    <Modal
      show={show}
      onHide={onHide}
      aria-labelledby="move-configuration-modal-title"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title as="h5" id="move-configuration-modal-title">
          {t(`${translationPrefix}.title.label`)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3 text-muted">
          {t(`${translationPrefix}.summary.label`, summaryValues)}
        </p>
        <Form.Group className={isDatapointsMove ? "mb-3" : ""}>
          <Form.Label>{t(topicFieldLabel)}</Form.Label>
          <Typeahead
            id="moveConfigurationTopic"
            key="moveConfigurationTopic"
            clearButton
            labelKey="name"
            selected={selectedTopic}
            onChange={(selected) => handleTopicChange(selected.length > 0 ? String(selected[0].id) : "")}
            options={availableTopics}
            isLoading={modalState.isLoadingTopics}
            disabled={modalState.isLoadingTopics || availableTopics.length === 0}
            placeholder={t(topicPlaceholder)}
            renderMenu={renderGroupedTopicMenu}
            inputProps={{ "aria-label": topicFieldLabel }}
          />
        </Form.Group>
        {isDatapointsMove && (
          <Form.Group>
            <Form.Label>{t(`${translationPrefix}.metric.label`)}</Form.Label>
            <Typeahead
              id="moveConfigurationMetric"
              key="moveConfigurationMetric"
              clearButton
              labelKey="name"
              selected={selectedMetric}
              onChange={(selected) => setModalState(prev => ({
                ...prev,
                destinationMetricId: selected.length > 0 ? String(selected[0].id) : "",
              }))}
              options={modalState.metricOptions}
              renderMenuItemChildren={(metric) => <span title={metric.name}>{metric.name}</span>}
              isLoading={modalState.isLoadingMetrics}
              disabled={
                !modalState.destinationTopicId
                || modalState.isLoadingMetrics
                || modalState.metricOptions.length === 0
              }
              placeholder={
                !modalState.destinationTopicId
                  ? t(`${translationPrefix}.selectTopicFirst.label`)
                  : modalState.isLoadingMetrics
                    ? t(`${translationPrefix}.loadingMetrics.label`)
                    : modalState.metricOptions.length === 0
                      ? t(`${translationPrefix}.noQuantitativeMetrics.label`)
                      : t(`${translationPrefix}.selectMetric.placeholder.label`)
              }
              inputProps={{ "aria-label": t(`${translationPrefix}.metric.label`) }}
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={modalState.isMoving}>
          {t("Cancel.label")}
        </Button>
        <Button
          variant="primary"
          onClick={handleMove}
          disabled={isMoveDisabled}
        >
          {t(`${translationPrefix}.move.label`)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default withTranslation()(MoveConfigurationModal);
